import React, { useState, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth, ROLE_CONFIG, UserRole } from '@/contexts/AuthContext'
import { useTheme } from '@/contexts/ThemeContext'
import { useNotifications } from '@/contexts/NotificationContext'
import { useFullscreen } from '@/hooks/useFullscreen'

interface NavItem {
  name: string
  href: string
  icon: string
  description: string
  minRole?: UserRole
  requiredRoles?: UserRole[]
}

export const ModernNavbar: React.FC = () => {
  const location = useLocation()
  const { user, logout, hasMinRole, hasRole, isAdmin } = useAuth()
  const { colorScheme, setTheme } = useTheme()
  const { notifications, unreadCount, clearAllNotifications, markAllAsRead, removeNotification } = useNotifications()
  const { isFullscreen, toggle: toggleFullscreen } = useFullscreen()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [isNotificationMenuOpen, setIsNotificationMenuOpen] = useState(false)

  // Navigation items with role requirements
  const allNavigation: NavItem[] = [
    { name: 'Dashboard', href: '/dashboard', icon: '📊', description: 'Overview & Analytics' },
    { name: 'POS Terminal', href: '/pos', icon: '🛒', description: 'Point of Sale' },
    { name: 'Bill Payments', href: '/bill-payments', icon: '💳', description: 'Utility & bill payments', minRole: 'cashier' },
    { name: 'Inventory', href: '/inventory', icon: '📦', description: 'Stock Management', minRole: 'manager' },
    { name: 'Customers', href: '/customers', icon: '👥', description: 'Customer Database', minRole: 'cashier' },
    { name: 'Procurement', href: '/procurement', icon: '🏭', description: 'Suppliers & goods receiving', minRole: 'manager' },
    { name: 'Expenses', href: '/expenses', icon: '💸', description: 'Expense Tracking', minRole: 'manager' },
    { name: 'Documents', href: '/documents', icon: '📄', description: 'Personal business documents', requiredRoles: ['admin'] },
    { name: 'Employees', href: '/employees', icon: '👤', description: 'Staff Management', requiredRoles: ['admin'] },
    { name: 'Transactions', href: '/transactions', icon: '📋', description: 'View past sales and receipts', minRole: 'cashier' },
    { name: 'Reports', href: '/reports', icon: '📈', description: 'Business Reports & AI Insights', minRole: 'manager' },
  ]

  // Filter navigation based on user role
  const navigation = useMemo(() => {
    return allNavigation.filter(item => {
      // If no role requirements, show to everyone
      if (!item.minRole && !item.requiredRoles) return true
      
      // Check required roles (exact match)
      if (item.requiredRoles && item.requiredRoles.length > 0) {
        return item.requiredRoles.some(role => hasRole(role))
      }
      
      // Check minimum role
      if (item.minRole) {
        return hasMinRole(item.minRole)
      }
      
      return true
    })
  }, [hasMinRole, hasRole])

  // Get role badge config
  const roleConfig = user?.role ? ROLE_CONFIG[user.role] : null

  const handleLogout = () => {
    logout()
    setIsUserMenuOpen(false)
  }

  const toggleTheme = () => {
    setTheme(colorScheme === 'dark' ? 'light' : 'dark')
  }

  return (
    <>
      {/* Modern Top Navigation - full width */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50 shadow-lg">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-10">
          <div className="flex items-center justify-between h-16 gap-2">
            {/* Brand - left */}
            <Link
              to="/dashboard"
              className="flex-shrink-0 flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-background/50 transition-colors"
            >
              <span className="text-base font-bold esales-text font-display whitespace-nowrap">Sachin Bookshop</span>
            </Link>

            {/* Desktop Navigation - center, takes remaining space */}
            <div className="hidden lg:flex flex-1 items-center justify-center gap-1 min-w-0">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`relative px-2.5 py-2 rounded-lg transition-all duration-200 group ${location.pathname === item.href
                    ? 'bg-primary/10 text-primary shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                    }`}
                >
                  <div className="flex items-center space-x-1.5">
                    <span className="text-sm">{item.icon}</span>
                    <span className="text-xs font-medium whitespace-nowrap">{item.name}</span>
                  </div>

                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-foreground text-background text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
                    {item.description}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-foreground"></div>
                  </div>

                  {/* Active indicator */}
                  {location.pathname === item.href && (
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-primary rounded-full"></div>
                  )}
                </Link>
              ))}
            </div>

            {/* Right Section - fullscreen, theme, notifications, user */}
            <div className="flex flex-shrink-0 items-center gap-2 sm:gap-4">
              {/* Fullscreen Toggle */}
              <button
                onClick={toggleFullscreen}
                className="relative p-2 rounded-lg bg-background/50 border border-border/50 hover:bg-background/80 transition-all duration-200 group"
                title={isFullscreen ? 'Exit full screen' : 'Full screen'}
              >
                <span className="text-base">{isFullscreen ? '⤓' : '⤢'}</span>
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-foreground text-background text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
                  {isFullscreen ? 'Exit full screen' : 'Full screen'}
                </div>
              </button>

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="relative p-2 rounded-lg bg-background/50 border border-border/50 hover:bg-background/80 transition-all duration-200 group"
                title={`Switch to ${colorScheme === 'dark' ? 'light' : 'dark'} mode`}
              >
                <span className="text-base">
                  {colorScheme === 'dark' ? '☀️' : '🌙'}
                </span>
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-foreground text-background text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
                  {colorScheme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                </div>
              </button>

              {/* Notifications */}
              <div className="relative">
                <button
                  onClick={() => {
                    const next = !isNotificationMenuOpen
                    setIsNotificationMenuOpen(next)
                    if (next && unreadCount > 0) {
                      markAllAsRead()
                    }
                  }}
                  className="relative p-2 rounded-lg bg-background/50 border border-border/50 hover:bg-background/80 transition-all duration-200 group"
                >
                  <span className="text-base">🔔</span>
                  {unreadCount > 0 && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-error rounded-full flex items-center justify-center">
                      <span className="text-xs text-white font-bold">{unreadCount}</span>
                    </div>
                  )}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-foreground text-background text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
                    Notifications
                  </div>
                </button>

                {/* Notification Dropdown */}
                {isNotificationMenuOpen && (
                  <div className="absolute right-0 mt-2 w-[min(20rem,calc(100vw-2rem))] bg-card border border-border rounded-xl shadow-xl z-50">
                    <div className="p-4 border-b border-border">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
                        {notifications.length > 0 && (
                          <button
                            onClick={clearAllNotifications}
                            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                          >
                            Clear All
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="max-h-96 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="p-6 text-center">
                          <span className="text-4xl mb-2 block">🔔</span>
                          <p className="text-sm text-muted-foreground">No notifications</p>
                        </div>
                      ) : (
                        <div className="p-2 space-y-2">
                          {notifications.map((notification) => (
                            <div
                              key={notification.id}
                              className={`p-3 rounded-lg border transition-colors ${notification.type === 'success'
                                ? 'bg-success/5 border-success/20'
                                : notification.type === 'warning'
                                  ? 'bg-warning/5 border-warning/20'
                                  : notification.type === 'error'
                                    ? 'bg-error/5 border-error/20'
                                    : 'bg-info/5 border-info/20'
                                }`}
                            >
                              <div className="flex items-start space-x-3">
                                <div className="text-lg flex-shrink-0">
                                  {notification.type === 'success' ? '✅' :
                                    notification.type === 'warning' ? '⚠️' :
                                      notification.type === 'error' ? '❌' : 'ℹ️'}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className={`text-sm font-medium mb-1 ${notification.read ? 'text-foreground' : 'text-primary'}`}>
                                    {notification.title}
                                  </h4>
                                  <p className="text-xs text-muted-foreground mb-2">
                                    {notification.message}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {notification.timestamp.toLocaleTimeString()}
                                  </p>
                                </div>
                                <button
                                  onClick={() => removeNotification(notification.id)}
                                  className="text-muted-foreground hover:text-foreground transition-colors"
                                  title="Dismiss"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-3 p-2 rounded-lg bg-background/50 border border-border/50 hover:bg-background/80 transition-all duration-200 group"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                    <span className="text-white text-sm font-medium">{user?.name?.charAt(0) || 'U'}</span>
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-xs font-medium text-foreground">{user?.name || 'User'}</p>
                    <p className={`text-xs font-medium ${roleConfig?.color || 'text-muted-foreground'}`}>
                      {roleConfig?.label || user?.role || 'User'}
                    </p>
                  </div>
                  <span className="text-muted-foreground group-hover:text-foreground transition-colors">
                    ▼
                  </span>
                </button>

                {/* User Dropdown */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-[min(16rem,calc(100vw-2rem))] bg-card border border-border rounded-xl shadow-xl z-50">
                    <div className="p-4 border-b border-border">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                          <span className="text-white text-xl font-medium">{user?.name?.charAt(0) || 'U'}</span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground">{user?.name || 'User'}</p>
                          <p className="text-xs text-muted-foreground">{user?.email || 'user@example.com'}</p>
                          <span className={`inline-block px-2 py-1 ${roleConfig?.bgColor || 'bg-primary/20'} ${roleConfig?.color || 'text-primary'} text-xs font-medium rounded-full mt-1`}>
                            {roleConfig?.label || user?.role || 'User'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="p-2">
                      <Link
                        to="/profile"
                        className="flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-background/50 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <span className="text-sm">👤</span>
                        <span className="text-sm">Profile Settings</span>
                      </Link>
                      {isAdmin && (
                        <Link
                          to="/settings"
                          className="flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-background/50 transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <span className="text-sm">⚙️</span>
                          <span className="text-sm">System Settings</span>
                        </Link>
                      )}
                      <Link
                        to="/help"
                        className="flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-background/50 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <span className="text-sm">❓</span>
                        <span className="text-sm">Help & Support</span>
                      </Link>
                      <div className="border-t border-border my-2"></div>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-error/10 text-error transition-colors"
                      >
                        <span className="text-sm">🚪</span>
                        <span className="text-sm">Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg bg-background/50 border border-border/50 hover:bg-background/80 transition-all duration-200"
              >
                <span className="text-base">
                  {isMobileMenuOpen ? '✕' : '☰'}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-border/50 bg-background/95 backdrop-blur-xl">
            <div className="px-4 py-4 space-y-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${location.pathname === item.href
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                    }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span className="text-lg">{item.icon}</span>
                  <div>
                    <p className="text-sm font-medium">{item.name}</p>
                    <p className="text-xs text-muted-foreground">{item.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Click outside to close dropdowns */}
      {(isUserMenuOpen || isMobileMenuOpen || isNotificationMenuOpen) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setIsUserMenuOpen(false)
            setIsMobileMenuOpen(false)
            setIsNotificationMenuOpen(false)
          }}
        />
      )}
    </>
  )
}
